import { DefaultMap } from '@seaber/common/lib/collections';
import { toM3 } from '../../constants/VolumeUnits';
import type { ShipId } from '../../graphql';
import {
  VolumeUnits,
  type VesselSpeedType,
  type VesselArea,
  type VesselSpeedConsumptionFragment,
  type VesselDraftDatapointFragment,
  type VesselSpeedTag,
  type ScenarioTimeline_ShipFragment,
  WeightUnits,
} from '../../graphql';
import { getShipWeightCapacity } from './Core';
import { omit } from 'lodash';
import { memoize } from '@seaber/common/lib/memoize';
import type { MassVolumeSize } from './Capacity';
import type { CubicMeters, MetricTonnes } from '../../constants/cargoUnits';

export type VSCSByTypeAndArea = Map<
  VesselSpeedType,
  Map<VesselArea | null, VesselSpeedConsumptionFragment[]>
>;

export type VSCSByTagTypeAndArea = Map<
  VesselSpeedTag | null,
  VSCSByTypeAndArea
>;

export type Vessel = ScenarioTimeline_ShipFragment & {
  speedConsumptions: VSCSByTypeAndArea;
  speedConsumptionsByTag: VSCSByTagTypeAndArea;
  draftDatapoints: VesselDraftDatapointFragment[];
};
export const defaultVessel: Vessel = {
  vesselAttributesAssociationsByShipId: { nodes: [] },
  vesselSpeedConsumptionsByShipId: { nodes: [] },
  cargoCompartmentsByShipId: { nodes: [] },
  id: NaN as ShipId,
  volumeCapacity: 100000,
  volumeUnit: VolumeUnits.M_3,
  weightCapacity: 100000,
  massCapacity: 100000,
  massUnit: WeightUnits.TONNE,
  inactive: false,
  speedConsumptions: new DefaultMap(() => new DefaultMap(() => [])),
  speedConsumptionsByTag: new Map(),
  shipVoyageNamingRuleRelationsByShipId: { nodes: [] },
  tcContractsByVesselId: { nodes: [] },
  vesselDraftDatapointsByShipId: { nodes: [] },
  draftDatapoints: [],
  vesselFulfillsRequirementAssociationsByVesselId: { nodes: [] },
  enableConsecutiveCoaVoyages: false,
};

export type NormalizedVessel = Omit<
  Vessel,
  'massCapacity' | 'volumeCapacity' | 'massUnit' | 'volumeUnit'
> & {
  capacity: MassVolumeSize;
};

export const normalizeVessel = memoize((vessel: Vessel): NormalizedVessel => {
  const { massCapacity, volumeCapacity } = getVesselCapacities(vessel);
  return {
    ...omit(vessel, [
      'massCapacity',
      'volumeCapacity',
      'massUnit',
      'volumeUnit',
      'weightCapacityType',
    ]),
    capacity: {
      mass: massCapacity,
      volume: volumeCapacity,
    },
  };
});

/** Returns vessel capacities in metric tonnes and cubic meters. 0 if unspecified. */
export function getVesselCapacities(
  vessel: ScenarioTimeline_ShipFragment | null,
): {
  massCapacity: MetricTonnes;
  massCapacitySource: 'DWCC' | 'DWT' | 'fallback' | null;
  volumeCapacity: CubicMeters;
  deckVolumeCapacity: CubicMeters;
} {
  const massCapacity: MetricTonnes = vessel?.massCapacity
    ? getShipWeightCapacity(
        +vessel.massCapacity,
        vessel.massUnit ?? WeightUnits.TONNE,
      )
    : ((vessel?.weightCapacity ?? 0) as MetricTonnes);

  const massCapacitySource = vessel?.massCapacity
    ? 'DWCC'
    : vessel?.weightCapacity
      ? 'DWT'
      : 'fallback';

  const volumeCapacity =
    vessel?.volumeCapacity && vessel.volumeUnit
      ? toM3(+vessel.volumeCapacity, vessel.volumeUnit)
      : (0 as CubicMeters);

  const deckVolumeCapacity =
    vessel?.deckVolumeCapacity && vessel.volumeUnit
      ? toM3(+vessel.deckVolumeCapacity, vessel.volumeUnit)
      : (0 as CubicMeters);

  return {
    massCapacity,
    massCapacitySource,
    volumeCapacity,
    deckVolumeCapacity,
  };
}

/** Returns a user-facing error message if the vessel is missing mass or volume capacity, or null if both are set. */
export function getVesselCapacityError(vessel: Vessel): string | null {
  const { massCapacity, volumeCapacity } = getVesselCapacities(vessel);
  if (massCapacity && volumeCapacity) return null;
  const missing =
    !massCapacity && !volumeCapacity
      ? 'mass and volume capacity'
      : !massCapacity
        ? 'mass capacity'
        : 'volume capacity';
  return `Vessel "${vessel.name}" has no ${missing} defined. Set it on the vessel before assigning orders.`;
}

/** Get set of channel names that are banned for a vessel based on channel incompatibilities */
export function getVesselBannedChannels(vessel: Vessel | null): Set<string> {
  const bannedChannels = new Set<string>();

  if (!vessel) {
    return bannedChannels;
  }

  vessel.vesselAttributesAssociationsByShipId?.nodes?.forEach((assoc) => {
    assoc?.vesselAttributeByVesselAttributeId?.channelIncompatibilitiesByVesselAttributeId?.nodes?.forEach(
      (n) => {
        if (n?.channelName) {
          bannedChannels.add(n.channelName.toLowerCase());
        }
      },
    );
  });

  return bannedChannels;
}
